<template>
    <div class="border-2 rounded-xl bg-white flex flex-col md:w-full mt-5">
        <div class="flex flex-col gap-7.5 md:p-10 p-5 justify-start">
            <div class="flex flex-col gap-8">
                <div class="flex flex-col justify-center items-start gap-7.5 h-auto w-full">
                    <h2 class="display-serif capitalize">{{ feedbackData?.feedback_question.question }}</h2>
                    <!-- Widget Row -->
                    <div class="flex flex-row justify-center items-stretch gap-10">
                        <!-- Stars -->
                        <div class="flex flex-col gap-2.5 justify-stretch items-start">
                            <div class="body-small text-black">Overall Rating</div>
                            <div v-if="feedbackData?.feedback_summary.average_rating" class="flex flex-row gap-1 relative">
                                <i v-for="n in 5" :key="n" class="bi bi-star-fill text-[#EAE9E9] px-0.5" aria-hidden="true"></i>
                                <div class="flex flex-row gap-1 overflow-x-hidden absolute" :style="{ width: `${feedbackData?.feedback_summary.average_rating * 20}%` }">
                                    <i v-for="n in 5" :key="n" class="bi bi-star-fill text-[#B6BBF8] px-0.5" aria-hidden="true"></i>
                                </div>
                            </div>
                            <div v-else class="body-small text-black pl-1">No ratings.</div>
                        </div>
                        <!-- Feedback Count -->
                        <div class="flex flex-col justify-center items-start">
                            <div class="body-small text-black">Participants</div>
                            <div class="font-serif text-[48px]/none text-[#B6BBF8]">
                                {{ feedbackData?.feedback_response_count }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col gap-1">
                    <div class="body-small-semibold text-black">Overview</div>
                    <div class="body-small text-black">{{ feedbackData?.feedback_summary.theme_summary }}</div>
                </div>
                <!-- Next row growth and strengths -->
                <div class="flex flex-row justify-around items-start gap-5">
                    <div class="flex flex-col w-full gap-1">
                        <div class="flex flex-row gap-2 justify-start items-center">
                            <i class="bi bi-trophy text-base" aria-hidden="true"></i>
                            <div class="body-small-semibold text-black">Top Strengths</div>
                        </div>
                        <template v-if="topStrengths.length > 0">
                            <div class="px-3 body-small text-black">
                                <ul class="pl-2 m-0">
                                    <li v-for="(strength, index) in topStrengths" :key="index" class="m-0!">{{ strength }}</li>
                                </ul>
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-3 pt-2 pl-6 body-small text-black">Not enough data.</div>
                        </template>
                    </div>
                    <div class="flex flex-col w-full">
                        <div class="flex flex-row gap-2 justify-start items-center">
                            <i class="bi bi-signpost-split text-base" aria-hidden="true"></i>
                            <div class="body-small-semibold text-black">Growth Areas</div>
                        </div>
                        <template v-if="growthAreas.length > 0">
                            <div class="px-3 body-small text-black">
                                <ul class="p-2 m-0">
                                    <li v-for="(growth, index) in growthAreas" :key="index" class="m-0!">{{ growth }}</li>
                                </ul>
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-3 pt-2 pl-6 body-small text-black">Not enough data.</div>
                        </template>
                    </div>
                </div>
                <div class="flex flex-row justify-around items-start gap-5">
                    <div class="flex flex-col w-full">
                        <div class="flex flex-row gap-2 justify-start items-center">
                            <i class="bi bi-question-circle text-base" aria-hidden="true"></i>
                            <div class="body-small-semibold text-black">Mixed Signals</div>
                        </div>
                        <template v-if="mixedSignals.length > 0">
                            <div class="px-3 body-small text-black">
                                <ul class="pl-2 m-0">
                                    <li v-for="(signal, index) in mixedSignals" :key="index" class="m-0!">{{ signal }}</li>
                                </ul>
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-3 pt-2 pl-6 body-small text-black">Not enough data.</div>
                        </template>
                    </div>
                    <div class="flex flex-col w-full">
                        <div class="flex flex-row gap-2 justify-start items-center">
                            <i class="bi bi-magic text-base" aria-hidden="true"></i>
                            <div class="body-small-semibold text-black">Illustrative Examples</div>
                        </div>
                        <template v-if="feedbackData?.feedback_summary.illustrative_examples.length > 0">
                            <div class="px-3 body-small text-black">
                                <ul class="p-2 m-0">
                                    <li v-for="(example, index) in feedbackData?.feedback_summary.illustrative_examples" :key="index" class="m-0!">{{ example }}</li>
                                </ul>
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-3 pt-2 pl-6 body-small">Not enough data.</div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { FeedbackData } from "./types";

type FeedbackSummaryReportProps = {
    feedbackData: FeedbackData;
};

const { feedbackData } = defineProps<FeedbackSummaryReportProps>();

const topStrengths = computed(() => feedbackData?.feedback_summary.top_strengths ?? []);
const growthAreas = computed(() => feedbackData?.feedback_summary.growth_areas ?? []);
const mixedSignals = computed(() => feedbackData?.feedback_summary.mixed_signals ?? []);
</script>
